/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { browserPolicyNames, ospPolicyNames } from 'client/util/Constants/OsPatchMgmtConstants';
import { isEmpty } from 'lodash';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import CollectionModule from 'client/store/modules/CollectionModule';
import PolicyApi, { createPolicyApi } from 'client/api/v2/PolicyApi';
import Vue from 'vue';

export class PolicyCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    module.actions.filterBoundResults = (...params) => this.filterBoundResults(...params);
    module.actions.selectItems = (...params) => this.selectItems(...params);
    module.actions.getPatchPoliciesCount = (...params) => this.getPatchPoliciesCount(...params);
    module.actions.setSortField = (...params) => this.setSortField(...params);
    module.actions.setSortOrder = (...params) => this.setSortOrder(...params);
    module.actions.sortCollectionByDelayDays = (...params) => this
      .sortCollectionByDelayDays(...params);
    module.actions.setPoliciesConfigFields = (...params) => this
      .setPoliciesConfigFields(...params);

    module.getters.getBoundItemsCollection = (...params) => this.getBoundItemsCollection(...params);

    module.mutations.REMOVE_COLLECTION_IDS = (...params) => this.REMOVE_COLLECTION_IDS(...params);
    module.mutations.SET_COLLECTION_SORT = (...params) => this.SET_COLLECTION_SORT(...params);
    module.mutations.SET_POLICIES_CONFIG_FIELDS = (...params) => this
      .SET_POLICIES_CONFIG_FIELDS(...params);
    module.mutations.SET_SORT_ORDER = (...params) => this
      .SET_SORT_ORDER(...params);
    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = super.getInitialState();
    state.policiesConfigFields = state.policiesConfigFields || {};
    state.sortOrder = state.sortOrder || 'asc';
    state.features.items.filters = [
      {
        name: 'template.osMetaFamily',
        operator: 'eq',
        value: '',
      },
    ];

    return state;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  /**
   * Override method from base class to ignore applied filters when determining
   * whether the Policy collection can be refreshed.
   */
  canRefreshCollection(state = { features: { items: {} } }, getters = {}) {
    const { features } = state;
    const { boundItemsUrl, showBoundItems } = features;
    const { searchTerm } = features.items;
    const { getItemsToBind = [], getItemsToUnbind = {} } = getters;
    const hasSelectionChanged = getItemsToBind.length > 0 || getItemsToUnbind.length > 0;

    return !((boundItemsUrl && showBoundItems) || searchTerm || hasSelectionChanged);
  }

  /**
   * Gets the list of bound items, similar to getCollection from the CollectionModule.
   */
  getBoundItemsCollection(state = { entities: { boundItems: { byId: {} } } }) {
    const boundItemIds = Object.keys(state.entities.boundItems.byId);
    return boundItemIds.reduce((accumulator, id) => {
      accumulator.push(state.entities.boundItems.byId[id]);
      return accumulator;
    }, []);
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------
  /**
   * Removes the list of ids from the item collections in the state, also updates totalCounts
   */
  REMOVE_COLLECTION_IDS(state, ids) {
    const removeItem = (items = [], id = '') => {
      const index = items.indexOf(id);

      if (index !== -1) {
        items.splice(index, 1);
      }
    };
    let removedItemCount = 0;
    let removedBoundItemCount = 0;
    ids.forEach(id => {
      const itemExists = !!state.entities.items.byId[id];
      const boundItemExists = !!state.entities.boundItems.byId[id];

      if (itemExists) {
        Vue.delete(state.entities.items.byId, id);
        removeItem(state.features.items.ids, id);
        removedItemCount += 1;
      }
      if (boundItemExists) {
        Vue.delete(state.entities.boundItems.byId, id);
        removedBoundItemCount += 1;
      }
    });
    const updatedTotalCount = state.entities.items.totalCount - removedItemCount;
    const updatedCountOfAllItems = state.entities.items.totalCountOfAllItems - removedItemCount;
    const updatedBoundTotalCount = state.entities.boundItems.totalCount - removedBoundItemCount;

    Vue.set(state.entities.items, 'totalCount', updatedTotalCount);
    Vue.set(state.entities.boundItems, 'totalCount', updatedBoundTotalCount);
    if (updatedCountOfAllItems > 0) {
      Vue.set(state.entities.items, 'totalCountOfAllItems', updatedCountOfAllItems);
    }
    if (updatedCountOfAllItems < updatedTotalCount) {
      Vue.set(state.entities.items, 'totalCountOfAllItems', updatedTotalCount);
    }
  }

  SET_FILTER(state, { filter, pagination }) {
    const { filters } = state.features.items;

    // If the filter already exists, update the value unless the operator is 'ne'.
    // there is currently a bug in the API with 'ne' and the | separator, so any
    // 'ne' filters need to be combined, not overwritten if one exists already.
    const existingFilterIndex = filters.findIndex(d => (
      d.name === filter.name && d.operator === filter.operator && d.operator !== 'ne'
    ));
    if (existingFilterIndex > -1) {
      Vue.set(state.features.items.filters, existingFilterIndex, filter);
      return;
    }

    // avoid adding the same exact filter condition if it already exists
    const existingNotEqualFilterIndex = filters.findIndex(d => (
      d.name === filter.name
      && d.operator === filter.operator
      && d.operator === 'ne'
      && d.value === filter.value
    ));

    if (existingNotEqualFilterIndex > -1) {
      return;
    }
    // If the filter doesn't exist or it exists but it is a 'ne' operator, add the filter
    filters.push(filter);
    Vue.set(state.features.items, 'filters', filters);

    if (pagination) {
      const { limit } = pagination;
      state.features.items.pagination.limit = limit;
    }
  }

  SET_POLICIES_CONFIG_FIELDS(state, policiesConfigFields) {
    state.policiesConfigFields = policiesConfigFields;
  }

  SET_SORT_ORDER(state, sortOrder) {
    state.sortOrder = sortOrder;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------
  /**
 * Applies local filters to bound policy results stored in the local collection
 */
  filterBoundResults(context) {
    const { filters } = context.state.features.items;
    const currentCollection = this.getBoundItemsCollection(context.state);
    const appliedFilters = filters.filter(filter => (filter.value !== ''));

    if (appliedFilters && appliedFilters.length) {
      let filteredItems = currentCollection;

      appliedFilters.forEach((filter) => {
        let filterFunction = () => true;
        const filterTerms = filter.name.split('.');

        if (filter.operator === 'in' && filter.name === 'template.os_meta_family') {
          const firstLevel = filterTerms[0];
          const secondLevel = 'osMetaFamily';
          filterFunction = item => (
            !filter.value.includes(item[firstLevel][secondLevel].toUpperCase())
          );
        }

        filteredItems = filteredItems.filter(filterFunction);
      });
      const filteredIds = filteredItems.map(item => item.id);

      context.commit('REMOVE_COLLECTION_IDS', filteredIds);
      context.commit('BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET');
    }
  }

  /**
   * Override setFilter method to allow calls that avoid resetting the loaded collection when
   * each filter is set.
   */
  setFilter(context, { filter, pagination, reset = true }) {
    if (reset) {
      context.commit('RESET_COLLECTION', { soft: true });
    }
    context.commit('SET_FILTER', { filter, pagination });
  }

  deleteItems(context, { ids, isOsPatchPolicy }) {
    return super.deleteItems(context, ids).then(data => {
      if (isOsPatchPolicy) {
        // We still need to check for other types of patch policies
        this.getPatchPoliciesCount().then(count => {
          if (count === 0 && context.rootGetters['FeatureTrialModule/getFeatureUsage'](trialFeatures.osPatchManagement.featureCode)) {
            context.dispatch('FeatureTrialModule/fetchFeatureUsage', { featureCode: trialFeatures.osPatchManagement.featureCode }, { root: true });
          }
        });
      }

      return data;
    });
  }

  selectItems(context, { ids }) {
    context.commit('SELECT_ITEMS', { ids });
  }

  getPatchPoliciesCount() {
    return PolicyApi.findPoliciesByName([...ospPolicyNames, ...browserPolicyNames])
      .then(policies => policies.totalCount);
  }

  /**
   * Set the sort field. If already sorting on the specified field the sort
   * direction will be reversed. If not already sorting on the specified field
   * then the sort field will be set. When sorting a soft reset is performed so
   * that any selected or modified items aren't lost.
   * Has flag for conditioning the reset of the collection.
   */
  setSortField(context, { sortField, reset = true }) {
    const { sort } = context.state.features.items;

    if (sortField === sort) {
      // Reverse sort direction
      sortField = sortField.split(' ').map((v) => {
        if (v.charAt(0) === '-') {
          return v.slice(1);
        }
        return `-${v}`;
      }).join(' ');
    }

    if (reset) context.commit('RESET_COLLECTION', { soft: true });
    context.commit('SET_SORT_FIELD', { sortField });
  }

  sortCollectionByDelayDays(context, { sortOrder, policiesConfigFields }) {
    const sortedCollection = context.getters.getCollection;
    sortedCollection.sort((a, b) => {
      const getDelayDaysConfigFieldId = (policy) => ({
        darwin: policiesConfigFields.darwin.osMinorUpdatesDeferral.delayDays,
        linux: policiesConfigFields.linux.osReleaseUpgradeDeferral.delayDays,
        windows: ((policiesConfigFields.windows || {}).daysToDeferUpdates || {}).delayDays,
      }[policy.template.osMetaFamily]);

      const getDarwinMinorUpdatesCheckboxValue = (policy) => {
        const checkboxId = policiesConfigFields.darwin.osMinorUpdatesDeferral.checkbox;
        return policy.values.find(({ configFieldID }) => configFieldID === checkboxId).value;
      };

      const getConfigFieldValue = (policy) => {
        let configFieldValue;
        const searchFn = ({ configFieldID }) => configFieldID === getDelayDaysConfigFieldId(policy);

        if (policy.template.osMetaFamily === 'darwin'
          && getDarwinMinorUpdatesCheckboxValue(policy) === false) {
          configFieldValue = 0;
        } else {
          configFieldValue = parseInt(policy.values.find(searchFn).value, 10);
        }

        return configFieldValue;
      };

      const configFieldAValue = getConfigFieldValue(a);
      const configFieldBValue = getConfigFieldValue(b);

      let sortValue = 0;
      if (configFieldAValue > configFieldBValue) sortValue = 1;
      if (configFieldBValue > configFieldAValue) sortValue = -1;

      if (sortOrder === 'desc') {
        sortValue *= -1;
      }

      return sortValue;
    });

    const items = {
      results: sortedCollection,
      totalCount: sortedCollection.length,
    };

    context.commit('RESET_COLLECTION', { soft: true });
    context.commit('SET_COLLECTION', items);
  }

  setPoliciesConfigFields(context, policiesConfigFields) {
    context.commit('SET_POLICIES_CONFIG_FIELDS', policiesConfigFields);
  }

  setSortOrder(context, sortOrder) {
    context.commit('SET_SORT_ORDER', sortOrder);
  }

  async fetchCollection(context, { next = false } = {}) {
    const { policiesConfigFields } = context.state;
    const { sort } = context.state.features.items;
    const { sortOrder } = context.state;
    const { boundItemsUrl } = context.state.features;

    // super.fetchCollection is called at the start,
    await super.fetchCollection(context, { next });

    if (sort === '' && !isEmpty(policiesConfigFields)) {
      this.sortCollectionByDelayDays(context, {
        sortOrder,
        policiesConfigFields,
      });
    }

    // only filter bound results when bound results can exist
    if (boundItemsUrl) {
      await context.dispatch('filterBoundResults');
    }
  }
}

export function createPolicyCollection(policyType) {
  return new PolicyCollectionClass(createPolicyApi(policyType), 'id', 'name').extractModule();
}
